import { useTimeGate } from '@exam-center/server-time';
import { AddAlarmOutlinedIcon, Button, type ButtonProps } from '@shared/components';
import { dayjs } from '@shared/utils';
import { useMemo } from 'react';

import type { ExamEventCourse, ExamEventSession } from '../../types';
import { getAdjustedSessionTime, getSessionLatestCourseCompletionTime } from '../../utils';

export interface ExtendTimeButtonProps extends Pick<ButtonProps, 'onClick' | 'loading'> {
  hidden: boolean;
  currentSession: ExamEventSession;
  participatedCourses: ExamEventCourse[];
  postponeDuration: number;
}

export default function ExtendTimeButton(props: Readonly<ExtendTimeButtonProps>) {
  const { loading, onClick, hidden, currentSession, participatedCourses, postponeDuration } = props;

  const adjustedSession = useMemo(
    () => (currentSession ? getAdjustedSessionTime(currentSession, postponeDuration) : null),
    [currentSession, postponeDuration],
  );

  const examDayStart = useMemo(
    () =>
      adjustedSession ? dayjs(adjustedSession.startedAt).startOf('day').toDate() : new Date(0),
    [adjustedSession],
  );
  const examDayEnd = useMemo(
    () => (adjustedSession ? dayjs(adjustedSession.startedAt).endOf('day').toDate() : new Date(0)),
    [adjustedSession],
  );
  const sessionStartDate = useMemo(
    () => adjustedSession?.startedAt ?? new Date(0),
    [adjustedSession],
  );
  const sessionEndDate = useMemo(() => adjustedSession?.endedAt ?? new Date(0), [adjustedSession]);
  const latestSessionCompletionDate = useMemo(
    () =>
      currentSession
        ? getSessionLatestCourseCompletionTime(
            participatedCourses,
            currentSession,
            postponeDuration,
          )
        : new Date(0),
    [participatedCourses, currentSession, postponeDuration],
  );

  const isOnOrAfterExamDay = useTimeGate(examDayStart);
  const isPastExamDay = useTimeGate(examDayEnd);
  const isPastSessionStart = useTimeGate(sessionStartDate);
  const isPastSessionEnd = useTimeGate(sessionEndDate);
  const isPastLatestSessionCompletion = useTimeGate(latestSessionCompletionDate);

  const isExamSessionToday = isOnOrAfterExamDay && !isPastExamDay;

  const shouldHide =
    hidden || loading || !currentSession || !isExamSessionToday || isPastLatestSessionCompletion;

  const shouldDisable = !currentSession || !isPastSessionStart || isPastSessionEnd;

  if (shouldHide) return null;

  return (
    <Button
      disabled={shouldDisable}
      onClick={onClick}
      sx={{ height: 'fit-content', minWidth: 'fit-content' }}
      startIcon={<AddAlarmOutlinedIcon />}
    >
      ทดเวลาสอบ
    </Button>
  );
}
